import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ChevronLeft, Copy, Plus, Search } from "lucide-react";
import SkillEditorForm, {
  type SkillFormData,
  skillValidationSchema,
} from "~/components/assistant/SkillEditorForm";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import useErrorToast from "~/contexts/error-toast";
import { cn } from "~/lib/utils";
import { useSkillLibrary } from "~/pages/datasets/components/assistant/hooks/useSkillLibrary";
import { DEFAULT_SKILL_CONTENT, type SkillFields } from "~/services/skills";

interface SkillPickerModalProps {
  open: boolean;
  onClose: () => void;
  /** Called when the user picks an existing library skill — POSTs `{ skill_id }`. */
  onAttachFromLibrary: (skillId: string) => Promise<void>;
  /** Called when the user writes a new skill — POSTs `{ fields }`. */
  onCreateSkill: (fields: SkillFields) => Promise<void>;
  onCreated: () => void;
}

const SkillPickerModal = ({
  open,
  onClose,
  onAttachFromLibrary,
  onCreateSkill,
  onCreated,
}: SkillPickerModalProps) => {
  const { showError } = useErrorToast();
  const [showEditor, setShowEditor] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [attaching, setAttaching] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const { skills: library, isLoading: libraryLoading } = useSkillLibrary();

  const {
    control,
    reset,
    handleSubmit,
    formState: { isValid },
  } = useForm<SkillFormData>({
    resolver: yupResolver(skillValidationSchema),
    mode: "onChange",
    defaultValues: { name: "", description: "", content: DEFAULT_SKILL_CONTENT },
  });

  useEffect(() => {
    if (!open) {
      setShowEditor(false);
      setSearch("");
    }
  }, [open]);

  const handleAddNew = () => {
    reset({ name: "", description: "", content: DEFAULT_SKILL_CONTENT });
    setShowEditor(true);
  };

  const handleAttachFromLibrary = async (skillId: string) => {
    setAttaching(skillId);
    try {
      await onAttachFromLibrary(skillId);
      onCreated();
    } catch (e) {
      console.error(e);
      showError(e instanceof Error ? e.message : "Unable to attach skill");
    } finally {
      setAttaching(null);
    }
  };

  const handleCreate = async (data: SkillFormData) => {
    setSubmitting(true);
    try {
      await onCreateSkill(data);
      onCreated();
    } catch (e) {
      console.error(e);
      showError(e instanceof Error ? e.message : "Unable to save skill");
    } finally {
      setSubmitting(false);
    }
  };

  const needle = search.trim().toLowerCase();
  const filtered = needle
    ? library.filter(
        (s) =>
          s.name.toLowerCase().includes(needle) ||
          (s.description?.toLowerCase().includes(needle) ?? false)
      )
    : library;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        className="sm:max-w-[640px] p-0 gap-0 h-[65vh] overflow-hidden flex flex-col"
        aria-describedby={undefined}
      >
        <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
          <DialogTitle className="flex items-center gap-1">
            {showEditor && (
              <button
                type="button"
                onClick={() => setShowEditor(false)}
                className="flex items-center justify-center rounded p-1.5 hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer"
                aria-label="Back to library"
              >
                <ChevronLeft className="size-4" />
              </button>
            )}
            {showEditor ? "Add New Skill" : "Browse Skills"}
          </DialogTitle>
        </DialogHeader>

        {showEditor ? (
          <SkillEditorForm control={control} />
        ) : (
          <>
            <div className="shrink-0 px-6 pb-3">
              <div className="relative">
                <Search
                  className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden="true"
                />
                <Input
                  placeholder="Search the library…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  aria-label="Search the library"
                />
              </div>
            </div>

            <div className="flex flex-1 flex-col gap-1.5 overflow-y-auto px-6 pb-6">
              <PickerRow
                icon={<Plus className="size-4" />}
                title="Create a new skill"
                description="Start from a blank template and write the instructions yourself."
                onClick={handleAddNew}
              />

              <div className="mt-3">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Library
                  {!libraryLoading && (
                    <span className="ml-1 font-normal text-muted-foreground/70">
                      ({filtered.length})
                    </span>
                  )}
                </span>
              </div>

              {libraryLoading ? (
                <div className="flex flex-col gap-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-16 animate-pulse rounded-md bg-muted" />
                  ))}
                </div>
              ) : filtered.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  {needle ? "No skills match your search." : "No skills in your library yet."}
                </p>
              ) : (
                <ul className="flex flex-col gap-1.5">
                  {filtered.map((skill) => (
                    <li key={skill.id}>
                      <PickerRow
                        icon={<Copy className="size-4" />}
                        title={skill.name}
                        description={skill.description}
                        onClick={() => handleAttachFromLibrary(skill.id)}
                        disabled={attaching !== null}
                        trailing={
                          <Badge
                            variant={skill.origin === "system" ? "default" : "muted"}
                            className="shrink-0 px-1.5 py-0 text-[0.625rem] uppercase tracking-wide"
                          >
                            {skill.origin === "system" ? "System" : "User"}
                          </Badge>
                        }
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}

        <DialogFooter className="px-6 pb-4 shrink-0">
          {showEditor && (
            <>
              <Button variant="outline" size="sm" onClick={() => setShowEditor(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit(handleCreate)}
                disabled={!isValid || submitting}
                loading={submitting}
                size="sm"
                className="min-w-[72px]"
              >
                Add
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface PickerRowProps {
  icon: React.ReactNode;
  title: string;
  description?: string;
  onClick: () => void;
  disabled?: boolean;
  trailing?: React.ReactNode;
}

const PickerRow = ({ icon, title, description, onClick, disabled, trailing }: PickerRowProps) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={cn(
      "group flex w-full cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 text-left text-foreground transition-colors hover:bg-accent",
      disabled && "cursor-not-allowed opacity-50 hover:bg-transparent"
    )}
  >
    <div className="flex size-8 shrink-0 items-center justify-center text-muted-foreground">
      {icon}
    </div>
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-medium">{title}</p>
      {description && <p className="line-clamp-2 text-xs text-muted-foreground">{description}</p>}
    </div>
    {trailing}
  </button>
);

export default SkillPickerModal;
